'use client'

import type { SearchLastSearchesListProps } from '@maestro/commerce-blocks/components/search-bar/search.types'

import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import { useLastSearchesList } from '@maestro/commerce-blocks/components/search-bar//batons/use-last-searches-list'
import { getSearchOptionId } from '@maestro/commerce-blocks/utils/search.utils'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer'
import { Link } from '@pearlconvert/ui/components/link'

export default function SearchLastSearchesList({
  lastSearches,
  hideSearch,
  removeSearch,
  setRemovalMessage,
  activeOptionId,
}: SearchLastSearchesListProps) {
  const t = useTranslations('commerce-blocks')
  const { handleRemoveSearch } = useLastSearchesList({
    removeSearch,
    setRemovalMessage,
  })
  const { publish } = useEventBusContext()

  const handleClickLastSearch = useCallback(
    (query: string, url: string) => {
      publish(EventType.SEARCH_SUGGESTIONS_CLICKED, {
        searchSection: `${t('search.last-searches')}`,
        searchTerm: query,
        clickUrl: url,
      })
      hideSearch()
    },
    [hideSearch, publish, t]
  )

  return (
    <div className="searchbar__suggestions-node">
      <h5 className="searchbar__suggestions-title">
        {t('search.last-searches')}
      </h5>
      <ul className="searchbar__suggestions-list searchbar__suggestions-list--last-searches">
        {lastSearches.map((search, index) => {
          const searchUrl = `/search/?text=${search}`
          const optionId = getSearchOptionId('last-searches', search, index)

          return (
            <li
              className="searchbar__suggestions-list-item"
              key={`SearchLastSearchesList-${search}-${index}`}
              id={optionId}
              role="option"
              aria-selected={activeOptionId === optionId}
            >
              <Link
                className="searchbar__suggestions-list-link"
                href={searchUrl}
                tabIndex={-1}
                onClick={() => {
                  handleClickLastSearch(search, searchUrl)
                }}
                aria-label={`${t('search.last-searches')}: ${search}`}
              >
                {search}
              </Link>
              <button
                className="searchbar__suggestions-list-button--delete"
                tabIndex={-1}
                onClick={(e) => {
                  handleRemoveSearch(e, index, search, lastSearches.length)
                }}
                aria-label={t('search.remove-query')}
                data-search-delete-index={index}
              >
                {t('search.delete-search-result', { search })}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
